import { useEffect, useState } from "react";
import { activityTotal, type UpdateJobView, type UpdatePrepareResponse } from "@racler/protocol";
import { Dialog } from "../../components/Dialog.tsx";
import {
  activityLines,
  channelLabel,
  needsRecheck,
  requestErrorText,
  UPDATE_WARNING,
  updateTexts,
} from "../../lib/desktopUpdates.ts";

interface Snapshot {
  data: UpdatePrepareResponse;
  /** Generated once per snapshot: double clicks and retries of the same confirmation reuse it (idempotent start). */
  requestId: string;
}

/**
 * Confirmation of ONE remote update. Opening it asks the Machine for a fresh, time-limited snapshot (versions + running
 * work); only an explicit confirmation of that snapshot starts the update.
 */
export function UpdateConfirmDialog({
  machineId,
  machineName,
  prepare,
  start,
  onClose,
}: {
  machineId: string;
  machineName: string;
  prepare: (machineId: string) => Promise<UpdatePrepareResponse>;
  start: (machineId: string, snapshotId: string, requestId: string) => Promise<UpdateJobView>;
  onClose: () => void;
}) {
  const [attempt, setAttempt] = useState(0);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recheck, setRecheck] = useState(false);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setChecking(true);
    setSnapshot(null);
    setError(null);
    setRecheck(false);
    setAck(false);
    prepare(machineId)
      .then((data) => {
        if (!cancelled) setSnapshot({ data, requestId: crypto.randomUUID() });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(requestErrorText(err));
        setRecheck(true);
      })
      .finally(() => !cancelled && setChecking(false));
    return () => {
      cancelled = true;
    };
  }, [prepare, machineId, attempt]);

  useEffect(() => {
    if (!snapshot) return;
    const ms = Date.parse(snapshot.data.expiresAt) - Date.now();
    setExpired(!(ms > 0));
    if (!(ms > 0)) return;
    const timer = setTimeout(() => setExpired(true), ms);
    return () => clearTimeout(timer);
  }, [snapshot]);

  const data = snapshot?.data ?? null;
  const total = data ? activityTotal(data.activity) : 0;
  const lines = data ? activityLines(data.activity) : [];
  const canConfirm = !!snapshot && !busy && !expired && !recheck && (total === 0 || ack);

  const confirm = () => {
    if (!snapshot || !canConfirm) return;
    setBusy(true);
    setError(null);
    start(machineId, snapshot.data.snapshotId, snapshot.requestId)
      .then(() => onClose())
      .catch((err: unknown) => {
        setError(requestErrorText(err));
        if (needsRecheck(err)) setRecheck(true);
      })
      .finally(() => setBusy(false));
  };

  return (
    <Dialog title={updateTexts.dialogTitle(machineName)} onClose={onClose}>
      <div className="dialog-body update-dialog" data-testid="update-dialog">
        {checking && <p className="muted small">{updateTexts.checking}</p>}
        {data && (
          <UpdateSnapshotDetails
            machineName={machineName}
            data={data}
            lines={lines}
            total={total}
            ack={ack}
            busy={busy}
            expired={expired}
            onAck={setAck}
          />
        )}
        {error && (
          <p className="error small" data-testid="dialog-error">
            {error}
          </p>
        )}
      </div>

      <div className="row end">
        <button type="button" onClick={onClose} autoFocus>
          {updateTexts.cancel}
        </button>
        {(recheck || expired) && !checking && (
          <button type="button" disabled={busy} onClick={() => setAttempt((a) => a + 1)}>
            {updateTexts.checkAgain}
          </button>
        )}
        <button
          type="button"
          className={total > 0 ? "danger" : "primary"}
          disabled={!canConfirm}
          onClick={confirm}
          data-testid="update-dialog-confirm"
        >
          {updateTexts.updateNow}
        </button>
      </div>
    </Dialog>
  );
}

/** The checked snapshot: versions, running work, the warning and (when work runs) the explicit acknowledgement. */
export function UpdateSnapshotDetails({
  machineName,
  data,
  lines,
  total,
  ack,
  busy,
  expired,
  onAck,
}: {
  machineName: string;
  data: UpdatePrepareResponse;
  lines: string[];
  total: number;
  ack: boolean;
  busy: boolean;
  expired: boolean;
  onAck: (ack: boolean) => void;
}) {
  return (
    <>
      <table className="kv">
        <tbody>
          <tr>
            <th>{updateTexts.machine}</th>
            <td>{machineName}</td>
          </tr>
          <tr>
            <th>{updateTexts.installedVersion}</th>
            <td>{data.installedVersion}</td>
          </tr>
          <tr>
            <th>{updateTexts.targetVersion}</th>
            <td>
              <strong>{data.targetVersion}</strong>
            </td>
          </tr>
          <tr>
            <th>{updateTexts.channel}</th>
            <td>{channelLabel(data.channel)}</td>
          </tr>
        </tbody>
      </table>

      {data.alreadyDownloaded && <p className="muted small">{updateTexts.alreadyDownloaded}</p>}

      {lines.length > 0 ? (
        <div className="small">
          <p className="update-activity-title">{updateTexts.runningWork}</p>
          <ul className="update-activity" data-testid="update-dialog-activity">
            {lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="muted small">{updateTexts.noActivity}</p>
      )}

      <p className={`update-warning${total > 0 ? " strong" : ""}`} data-testid="update-dialog-warning">
        {UPDATE_WARNING}
      </p>

      {total > 0 && (
        <label className="update-ack small">
          <input
            type="checkbox"
            checked={ack}
            disabled={busy}
            onChange={(e) => onAck(e.target.checked)}
            data-testid="update-dialog-ack"
          />
          {updateTexts.acknowledge}
        </label>
      )}

      {expired ? (
        <p className="status warn small">{updateTexts.expired}</p>
      ) : (
        <p className="muted small">{updateTexts.validUntil(data.expiresAt)}</p>
      )}
    </>
  );
}
